import { FUNNEL_TYPES } from "../utils/contants";
import { FunnelContextArea } from "../types";
import { useAccountData } from "../../../hooks/useAccountData";
import {
  SubscriptionModalBody,
  SubscriptionModalHeader,
} from "../../payments/SubscriptionModal";
import { SubscriptionModalV2 } from "../../payments/SubscriptionModalV2";
import { DonationModalHeader } from "../../payments/components/DonationModalHeader";
import { DonationModalBody } from "../../payments/components/DonationModalBody";
import { PdfModalHeader } from "../../payments/components/PdfModalHeader";
import { PdfModalBody } from "../../payments/components/PdfModalBody";
import { PdfModalFooter } from "../../payments/components/PdfModalFooter";
import { DownloadPdfModalBody } from "../../payments/components/DownloadPdfModalBody";
import { ReadingStatusModalBody } from "../../readingStatusButton/components/ReadingStatusModalBody";
import { ReadingStatusModalHeader } from "../../readingStatusButton/components/ReadingStatusModalHeader";
import { DonationConfirmationModalBody } from "../../payments/components/DonationConfirmationModalBody";
import { OnboardingModalBody } from "../../onboarding/OnboardingModalBody";
import { PurchaseReleaseModalHeader } from "../../payments/components/PurchaseReleaseModalHeader";
import { PurchaseReleaseModalBody } from "../../payments/components/PurchaseReleaseModalBody";
import { PurchaseBundleModalHeader } from "../../payments/components/PurchaseBundleModalHeader";
import { PurchaseBundleModalBody } from "../../payments/components/PurchaseBundleModalBody";
import { PurchaseGiftCardModalBody } from "../../payments/components/PurchaseGiftCardModalBody";
import {
  PrePaywallModalBody,
  PrePaywallModalHeader,
} from "../../payments/components/PrePaywallModalBody";
import { CongratsBody } from "../../../congrats/CongratsBody";
import { GoldAccessBody } from "../../goldAccess/GoldAccessBody";
import {
  BuildFunnelConfig,
  useBuildFunnel,
  UseBuildFunnelParams,
} from "./useBuildFunnel";
import {
  ChangeUsernameBody,
  ChangeUsernameHeader,
} from "../../ChangeUsernameBody";
import { BlackFridayCongratsBody } from "../../../screens/blackFriday/BlackFridayCongratsModalBody";
import { HolidayBundleCongratsBody } from "../../../screens/holidayBundle/HolidayBundleCongratsModalBody";
import { OfferPaywallModalBody } from "../../payments/components/OfferPaywallModalBody";
import { PurchasePodModalHeader } from "../../payments/components/PurchasePodModalHeader";
import { PurchasePodModalBody } from "../../payments/components/PurchasePodModalBody";


export function useContentFunnel(params: UseBuildFunnelParams) {
  const accountData = useAccountData();

  const buildFunnel = ({
    type,
    context,
    options,
    props,
  }: BuildFunnelConfig) => {
    let funnelHeader, funnelBody, funnelFooter;
    const funnelProps = {
      ...context,
      ...options,
      ...props,
      release: context?.purchaseRelease,
      bundle: context?.bundle,
      artistId: context?.donationArtistId,
    };

    if (type === FUNNEL_TYPES.subscription) {
      const isGiftContext =
        funnelProps.modalContext === FunnelContextArea.gift;
      if (isGiftContext) {
        funnelHeader = <SubscriptionModalHeader {...funnelProps} />;
        funnelBody = (
          <PurchaseGiftCardModalBody
            {...funnelProps}
            {...options?.componentProps}
          />
        );
      } else {
        const forceVariant = options?.componentProps?.forceCheckoutVariant as 'v1' | 'v2' | undefined;
        const isStandalonePageContext = [
          FunnelContextArea.gold,
          FunnelContextArea.valkyrae,
          FunnelContextArea.blackFridayDeal,
          FunnelContextArea.holidayBundle,
        ].includes(funnelProps.modalContext as FunnelContextArea);
        const isGuest = !accountData?.user;
        const useV2 = forceVariant
          ? forceVariant === 'v2'
          : !isStandalonePageContext &&
            (isGuest
              ? !!accountData?.features["webnext_new_paywall_guest"]
              : !!accountData?.features["webnext_new_paywall"]);
        const isPaywallHeader = !!accountData?.features["paywall_header"];
        if (useV2) {
          funnelBody = (
            <SubscriptionModalV2 {...funnelProps} {...options?.componentProps} isPaywallHeader={isPaywallHeader} />
          );
        } else {
          funnelHeader = <SubscriptionModalHeader {...funnelProps} />;
          funnelBody = (
            <SubscriptionModalBody
              {...funnelProps}
              displayExploreMoreContent
              {...options?.componentProps}
            />
          );
        }
      }
    } else if (type === FUNNEL_TYPES.offerPaywall) {
      funnelHeader = undefined; // No header for pre-subscription modal
      funnelBody = (
        <OfferPaywallModalBody
          {...funnelProps}
          assetUrl={accountData?.offer_paywall}
        />
      );
    } else if (type === FUNNEL_TYPES.donate) {
      funnelHeader = <DonationModalHeader {...funnelProps} />;
      funnelBody = <DonationModalBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.donateConfirmation) {
      funnelHeader = <DonationModalHeader {...funnelProps} />;
      funnelBody = <DonationConfirmationModalBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.pdf) {
      funnelHeader = <PdfModalHeader {...funnelProps} />;
      funnelBody = <PdfModalBody {...funnelProps} />;
      funnelFooter = <PdfModalFooter {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.downloadPdf) {
      funnelHeader = <PdfModalHeader {...funnelProps} />;
      funnelBody = <DownloadPdfModalBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.addToLibrary) {
      funnelHeader = <ReadingStatusModalHeader {...funnelProps} />;
      funnelBody = <ReadingStatusModalBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.onboarding) {
      const isNewOnboarding = !!accountData?.features["new_onboarding"];
      const newOnboarding = funnelProps.newOnboarding && isNewOnboarding;

      funnelBody = (
        <OnboardingModalBody {...funnelProps} newOnboarding={newOnboarding} />
      );
    } else if (type === FUNNEL_TYPES.purchaseRelease) {
      funnelHeader = <PurchaseReleaseModalHeader {...funnelProps} />;
      funnelBody = <PurchaseReleaseModalBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.purchaseBundle) {
      funnelHeader = <PurchaseBundleModalHeader {...funnelProps} />;
      funnelBody = <PurchaseBundleModalBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.prePaywall) {
      funnelHeader = <PrePaywallModalHeader {...funnelProps} />;
      funnelBody = <PrePaywallModalBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.congrats) {
      // Extract congrats config from componentProps
      const congratsProps = {
        customCongratsImage: options?.componentProps?.customCongratsImage,
        customCongratsText: options?.componentProps?.customCongratsText,
        customShelfCtaText: options?.componentProps?.customShelfCtaText,
        customCongratsCoverUrls: options?.componentProps?.customCongratsCoverUrls,
      };

      funnelBody = <CongratsBody {...funnelProps} {...congratsProps} />;
    } else if (type === FUNNEL_TYPES.blackFridayCongrats) {
      funnelBody = <BlackFridayCongratsBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.holidayBundleCongrats) {
      funnelBody = <HolidayBundleCongratsBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.goldAccess) {
      funnelBody = <GoldAccessBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.changeUsername) {
      funnelHeader = <ChangeUsernameHeader {...funnelProps} />;
      funnelBody = <ChangeUsernameBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.purchasePod) {
      funnelHeader = <PurchasePodModalHeader {...funnelProps} />;
      funnelBody = <PurchasePodModalBody {...funnelProps} />;
    } else if (type === FUNNEL_TYPES.purchasePodConfirmation) {
      // Confirmation is handled within PurchasePodModalBody
      funnelBody = <PurchasePodModalBody {...funnelProps} />;
    }

    return {
      funnelHeader,
      funnelBody,
      funnelFooter,
    };
  };

  return useBuildFunnel(buildFunnel, params);
}
